// ─── Demo-data isolation contract (Stage 3A Part 4) ─────────────
//
// THIS DIRECTORY is the only place demo / test / fake election-day
// data may live. The canned dataset Stage 3C will add lands here as
// `results.demo.ts`. Real data lives in Postgres, never in this
// directory. Demo data lives in code, never in Postgres.
//
// The four hard rules every component that consumes results data
// MUST respect:
//
//   1. SOURCE EXCLUSIVITY
//      Pick exactly ONE source per render based on
//      `useResultsDemoMode()`. Demo and real data are NEVER merged,
//      averaged, or composed in any way. There is no "use real where
//      we have it, fall back to demo" branch — it's all-real or all-
//      demo at the component boundary.
//
//   2. PHYSICAL SEPARATION
//      Real data is read only via the existing `useElectionDay*`
//      hooks (Postgres-backed, edge-cached). Demo data is read only
//      from this directory. Turning `results_demo_mode` off leaves
//      ZERO code path that can render a demo value. Clearing real
//      data is a separate one-tap admin reset (built with the entry
//      forms in Stage 3D); it does not touch demo data and vice
//      versa.
//
//   3. THE HERO WINNER IS REAL-DATA-ONLY, ALWAYS
//      The hero meta-slot winner announcement (Hero.tsx — Stage 3A
//      precedence rule) reads from `useElectionDayEditorial().data
//      .nationalCall` and that hook MUST NEVER be wrapped, mocked,
//      or replaced by a demo source. Demo mode populates only the
//      lower-stakes map/table visualisations. If an editor needs to
//      preview a hero call, they save a real nationalCall in the
//      editorial admin form — the toggle is irrelevant to it.
//
//   4. LOUD, UNMISSABLE BANNER
//      Whenever `results_demo_mode === 'live'`, the site-wide
//      DemoBanner (mounted in main.tsx) renders at the top of every
//      page, visible to the public. Stage 3C adds an additional
//      inline banner above each affected results widget so that
//      even a deep-linked screenshot can't masquerade as real.
//
// Any deviation from these rules is a P0 bug. If you find yourself
// reaching for a "convenient" exception, stop and surface it as a
// design question instead of writing the exception.

import { useFeatureFlags } from '../../lib/useFeatureFlags';

/** True when `results_demo_mode` is set to 'live' in the
 *  feature_flags table. Returns false during loading / on error
 *  (fail-safe — better to render empty real-data state than to
 *  accidentally serve demo data). */
export function useResultsDemoMode(): boolean {
  const flags = useFeatureFlags();
  return flags['results_demo_mode']?.status === 'live';
}
